import { DATA_SERVICE_BASE_URL } from '../ApiService';
import { fetchWithAuth } from '../AuthService';

/**
 * ML Analytics API — communicates with the AI-Service ML endpoints
 * via the data-service proxy at /api/proxy/ai-service/api/ml/*.
 *
 * Also includes direct data-service calls for persisted segment data
 * at /api/analytics/user-segments/*.
 */
const ML_BASE = `${DATA_SERVICE_BASE_URL}/api/proxy/ai-service/api/ml`;
const ANALYTICS_BASE = `${DATA_SERVICE_BASE_URL}/api/analytics`;

export const MLAnalyticsApi = {

  /** Get status/metadata for all ML models. */
  async getStatus() {
    const res = await fetchWithAuth(`${ML_BASE}/status`);
    if (!res.ok) throw new Error(`ML status failed: ${res.status}`);
    return res.json();
  },

  /** Get churn risk predictions (top N users). */
  async getChurnPredictions(top = 50) {
    const res = await fetchWithAuth(`${ML_BASE}/churn?top=${top}`);
    if (!res.ok) throw new Error(`Churn prediction failed: ${res.status}`);
    return res.json();
  },

  /** Get user segmentation assignments with cluster info. */
  async getSegments() {
    const res = await fetchWithAuth(`${ML_BASE}/segments`);
    if (!res.ok) throw new Error(`Segmentation failed: ${res.status}`);
    return res.json();
  },

  /** Get RFM segmentation with per-user scores + distribution. */
  async getRFMSegments() {
    const res = await fetchWithAuth(`${ML_BASE}/segments/rfm`);
    if (!res.ok) throw new Error(`RFM segmentation failed: ${res.status}`);
    return res.json();
  },

  /** Get GPT cost forecast for N days. */
  async getForecast(days = 30) {
    const res = await fetchWithAuth(`${ML_BASE}/forecast?days=${days}`);
    if (!res.ok) throw new Error(`Forecast failed: ${res.status}`);
    return res.json();
  },

  /** Get cost anomalies for the last N days. */
  async getCostAnomalies(days = 7) {
    const res = await fetchWithAuth(`${ML_BASE}/anomalies/cost?days=${days}`);
    if (!res.ok) throw new Error(`Cost anomaly detection failed: ${res.status}`);
    return res.json();
  },

  /** Get session anomalies for the last N days. */
  async getSessionAnomalies(days = 7) {
    const res = await fetchWithAuth(`${ML_BASE}/anomalies/sessions?days=${days}`);
    if (!res.ok) throw new Error(`Session anomaly detection failed: ${res.status}`);
    return res.json();
  },

  /** Get combined insights summary (churn + segments + forecast + anomalies). */
  async getInsights() {
    const res = await fetchWithAuth(`${ML_BASE}/insights`);
    if (!res.ok) throw new Error(`ML insights failed: ${res.status}`);
    return res.json();
  },

  /** Trigger model training (admin-only, requires JOBS_AUTH_TOKEN). */
  async trainAll() {
    const res = await fetchWithAuth(`${ML_BASE}/train`, { method: 'POST' });
    if (!res.ok) throw new Error(`Training failed: ${res.status}`);
    return res.json();
  },

  /** Train a specific model by name (churn, segments, rfm, forecast, anomaly). */
  async trainModel(modelName) {
    const res = await fetchWithAuth(`${ML_BASE}/train/${modelName}`, { method: 'POST' });
    if (!res.ok) throw new Error(`Training ${modelName} failed: ${res.status}`);
    return res.json();
  },

  // ── Persisted segment data (via data-service) ──────────────────

  /** Get persisted segment distribution (RFM + cluster counts). */
  async getSegmentDistribution() {
    const res = await fetchWithAuth(`${ANALYTICS_BASE}/user-segments/distribution`);
    if (!res.ok) throw new Error(`Segment distribution failed: ${res.status}`);
    return res.json();
  },

  /** Get persisted user segments with optional filtering. */
  async getUserSegments({ rfmSegment, clusterId, page = 0, pageSize = 50 } = {}) {
    const params = new URLSearchParams();
    if (rfmSegment) params.set('rfmSegment', rfmSegment);
    if (clusterId != null) params.set('clusterId', clusterId);
    params.set('page', page);
    params.set('pageSize', pageSize);
    const res = await fetchWithAuth(`${ANALYTICS_BASE}/user-segments?${params}`);
    if (!res.ok) throw new Error(`User segments query failed: ${res.status}`);
    return res.json();
  },

  /** Get a single user's segment assignment. */
  async getUserSegment(userId) {
    const res = await fetchWithAuth(`${ANALYTICS_BASE}/user-segments/${userId}`);
    if (!res.ok) throw new Error(`User segment fetch failed: ${res.status}`);
    return res.json();
  },

  /** Get user learning profiles (enriched view) with optional filtering. */
  async getUserProfiles({ learningStyle, subscriptionStatus, sortBy, sortDir, page = 0, pageSize = 20 } = {}) {
    const params = new URLSearchParams();
    if (learningStyle) params.set('learningStyle', learningStyle);
    if (subscriptionStatus) params.set('subscriptionStatus', subscriptionStatus);
    if (sortBy) params.set('sortBy', sortBy);
    if (sortDir) params.set('sortDir', sortDir);
    params.set('page', page);
    params.set('pageSize', pageSize);
    const res = await fetchWithAuth(`${ANALYTICS_BASE}/user-profiles?${params}`);
    if (!res.ok) throw new Error(`User profiles fetch failed: ${res.status}`);
    return res.json();
  },
};
